// Module-scope handler factories for use-agent-filter-panel — pulled out so
// the hook itself stays under the line-count threshold. Mirrors the
// keywords-filter-sidebar-handlers.ts / feedback-filter-sidebar-handlers.ts
// pattern (features/keywords/hooks/keywords-filter-sidebar-handlers.ts).
import { toast } from 'react-toastify'

import type { FilterControlProps } from 'features/charts-v2'
import type { SelectedFilterOption } from 'features/charts-v2/types'
import type { FilterOption } from 'features/filters/types'
import {
  applyFilterSelectionToCriteria,
  criteriaToSelectedFilterOptions,
  UNSUPPORTED_AGENT_FILTER_FIELDS,
} from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import type { FilterCriterion } from 'features/trend-detection/types'

const UNSUPPORTED_FIELD_TOAST =
  "This filter type isn't supported on agents yet."

export function buildUpdateFilterSelectionHandler(
  criteria: readonly FilterCriterion[],
  onCriteriaChange: (criteria: FilterCriterion[]) => void,
): FilterControlProps['updateFilterSelection'] {
  return (field, _, values, range) => {
    // Belt and suspenders: `narrowAgentFilterOptions` already keeps these
    // fields out of the picker's dimension list, so this only fires if a
    // stale selection or a future regression routes one through anyway.
    if (UNSUPPORTED_AGENT_FILTER_FIELDS.has(field)) {
      toast.info(UNSUPPORTED_FIELD_TOAST)
      return
    }
    onCriteriaChange(
      applyFilterSelectionToCriteria(criteria, field, values, range),
    )
  }
}

/** No-op write paths for the picker sections the agent create payload has no
 * home for (smart search, keyword logical search — see
 * `agent-filter-criteria.ts`'s "which filter types to expose" note). Passing
 * `smartSearchValue: null` / `keywordFilters: {}` keeps their cards from ever
 * rendering; these guard the add-filter popover's panes for those sections,
 * which stay reachable through the shared `FilterSelectionPopover` UI. */
export function buildUnsupportedSectionHandler<T>(): (value: T) => void {
  return () => toast.info(UNSUPPORTED_FIELD_TOAST)
}

/** Builds the field → SelectedFilterOption map both `getFilterSelection` and
 * `selectedFilterOptions` are derived from. A single memo input so both stay
 * referentially consistent, and so `getFilterSelection`'s identity changes
 * iff `criteria`/`filterOptions` change — the invalidation signal
 * `FilterControl`'s internal memos rely on (never `useCallback` this with an
 * empty dep array, which would freeze the rendered card list). */
export function buildSelectionsByField(
  criteria: readonly FilterCriterion[],
  filterOptions: readonly FilterOption[],
): Map<string, SelectedFilterOption> {
  return new Map(
    criteriaToSelectedFilterOptions(criteria, filterOptions).map((s) => [
      s.field,
      s,
    ]),
  )
}

/** Assembles the complete `FilterControlProps` bag. Kept here (not in the
 * hook) so `use-agent-filter-panel.ts` stays under the hook line-count
 * threshold — it just gathers the pieces and calls this. Mirrors
 * `keywords-filter-sidebar-handlers.ts`'s `buildFilterControlProps`. */
export function buildFilterControlProps(params: {
  filterOptions: readonly FilterOption[]
  isFilterOptionsLoading: boolean
  selectedFilterOptions: SelectedFilterOption[]
  updateFilterSelection: FilterControlProps['updateFilterSelection']
  getFilterSelection: FilterControlProps['getFilterSelection']
  getStaleInfo?: FilterControlProps['getStaleInfo']
  getValueLabel?: FilterControlProps['getValueLabel']
}): FilterControlProps {
  const {
    filterOptions,
    isFilterOptionsLoading,
    selectedFilterOptions,
    updateFilterSelection,
    getFilterSelection,
    getStaleInfo,
    getValueLabel,
  } = params
  return {
    filterOptions: [...filterOptions],
    isFilterOptionsLoading,
    selectedFilterOptions,
    updateFilterSelection,
    getFilterSelection,
    getStaleInfo,
    getValueLabel,
    // No ghost trio passed — this form has no "disable" affordance, only
    // add/remove. Omitting getGhostSelection/onToggleFilterDisabled/
    // onRemoveGhost hides FilterControl's eye toggle entirely.
    // No home in SavedFilters.criteria — see agent-filter-criteria.ts.
    // Passing null/{} keeps their cards from ever rendering; the no-op
    // handlers guard the popover's Smart search / Keywords panes, which
    // stay reachable through the shared FilterSelectionPopover UI.
    smartSearchValue: null,
    keywordFilters: {},
    updateSmartSearch: buildUnsupportedSectionHandler(),
    updateKeywordFilters: buildUnsupportedSectionHandler(),
    // The agent payload has no operator field — see AGENT_FILTER_OPERATOR.
    lockOperator: true,
    // This mounts inline in a 640px form, not a fixed-height sidebar.
    listMaxHeight: 'none',
    // Header row (heading + [+] add-filter action) is owned by
    // `AgentFilterPanel`, which portals FilterSelectHeader's own button into
    // its own header slot — see AgentFilterPanel's `headerActionsPortalTarget`
    // wiring. Leaving hideFilterHeaderRow/alwaysShowAddFilters unset restores
    // FilterControl's natural defaults: the dashed "+ Add filters" box shows
    // only while the section is genuinely empty.
    // No open room to the popover's right in a centered 640px form — see
    // FilterSelectionPopover's `anchorMode` doc comment.
    filterPopoverAnchorMode: 'center',
    // Already-applied filter cards read before the add-more affordance, not
    // after — see FilterControl's `addFiltersRowPosition` doc comment.
    addFiltersRowPosition: 'after',
  }
}
